import { useEffect, useRef } from "react"
import { notifyManager, useQueryClient } from "@tanstack/react-query"
import {
  isDateRangeWithinNormalPerformancePricingRefreshWindow,
} from "connected-components/dashboard/helpers/date-calculations"
import { getInstrumentSuffixForIntradayContributionPrefix } from "gatherers/contributors-data-integrator.utils"
import { isPinnedHistoricalBatch } from "gatherers/helpers"

function invalidateGatherers(queryClient, gatherers, startDate, endDate) {
  const withinRefreshWindow = isDateRangeWithinNormalPerformancePricingRefreshWindow(startDate, endDate)

  notifyManager.batch(() => {
    for (const gatherer of gatherers) {
      if (!gatherer?.key) continue

      const isContribution = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null
      if (!isContribution && !withinRefreshWindow) continue

      queryClient.invalidateQueries({
        queryKey: gatherer.graphqlKey ?? gatherer.key,
        exact: true,
        refetchType: "active",
      })
    }
  })
}

/**
 * Invalidates TanStack Query cache entries for active gatherers when either the committed-batch
 * `asOf` timestamp advances, or, absent a committed batch, when the live-price `intradayPricingAsOf`
 * clock advances. `intradayPricingAsOf` is a dashboard-wide clock (Redux `core` slice, written from
 * a `timestampUpdated` socket event in dashboard.jsx) — not a per-portfolio price signal.
 *
 * Eligibility rules:
 *  - Batch `asOf` trigger: skipped once `intradayBatchMetadata.id` is a genuine historical pin
 *    (diverges from `lastIntradayBatchId`) — a pinned batch owns its own refresh, not this tick.
 *  - Live-price `intradayPricingAsOf` trigger: only runs while NO batch is pinned. It stops the
 *    instant a batch is pinned, same as the batch trigger above never firing for a pinned state —
 *    the actual handoff to fresh data happens because gatherers key their queries on `batchId`
 *    (directly or via an intraday-simulation flag), so a batch commit changes the query key itself
 *    and TanStack Query fetches it as new. Neither `useEffect` here needs to fire at that instant.
 *  - `intradayContribution*` gatherers: always invalidated (fixed short periods, ungated).
 *  - All other gatherers: only invalidated when the date range is ≤ 1 month.
 */
export function useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId) {
  const queryClient = useQueryClient()
  const prevAsOfRef = useRef(intradayBatchMetadata?.asOf ?? null)
  const prevPricingAsOfRef = useRef(intradayPricingAsOf)

  useEffect(() => {
    const asOf = intradayBatchMetadata?.asOf
    if (!asOf || asOf === prevAsOfRef.current || isPinnedHistoricalBatch(intradayBatchMetadata?.id, lastIntradayBatchId)) return

    prevAsOfRef.current = asOf
    invalidateGatherers(queryClient, gatherers, startDate, endDate)
    // Full object dep: must re-run whenever .asOf or .id changes — both are read in the body.
    // (The second effect below narrows to `.id` only — that one only ever reads .id, so an
    // .asOf-only change there would be a same-render no-op re-run, not a missed update.)
  }, [intradayBatchMetadata, gatherers, queryClient, startDate, endDate, lastIntradayBatchId])

  useEffect(() => {
    if (isPinnedHistoricalBatch(intradayBatchMetadata?.id, lastIntradayBatchId)) return
    if (!intradayPricingAsOf || intradayPricingAsOf === prevPricingAsOfRef.current) return

    prevPricingAsOfRef.current = intradayPricingAsOf
    invalidateGatherers(queryClient, gatherers, startDate, endDate)
  }, [intradayBatchMetadata?.id, intradayPricingAsOf, lastIntradayBatchId, gatherers, queryClient, startDate, endDate])
}
